import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { useServices } from 'hooks/useServices';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { GiroPaymentManagerRateChangeFieldset } from 'containers/GiroPaymentManager/GiroPaymentManagerRateChangeFieldset';
import { useRateChangeForm } from 'hooks/useRateChangeForm';
import { validateRateChangeFormValues } from 'utils/rateChangeFormUtils';
import { useGiroPaymentManager } from 'hooks/useGiroPaymentManager';

type Props = {
  paymentId: number;
  onCancel(): void;
  onEdited(): void;
};

export const GiroPaymentManagerRateEditorScreen = (props: Props) => {
  const { paymentId, onCancel: cancel, onEdited } = props;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { data } = useGiroPaymentManager();

  const { updatePaymentExchangeRate, getExchangeRate } = useServices();

  const {
    values: formValues,
    violations: formViolations,
    setViolations: setFormViolations,
    setM8RateDTO,
    setNextDateOn,
  } = useRateChangeForm();

  React.useLayoutEffect(() => {
    if (data.giroPayment.exchangeRate) {
      setM8RateDTO(data.giroPayment.exchangeRate);
      setNextDateOn(data.giroPayment.exchangeRate.dateOn);
    }
  }, [data.giroPayment.exchangeRate, setNextDateOn, setM8RateDTO]);

  const {
    giroPaymentManager: { rateEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const submit = React.useCallback(() => {
    setFormViolations([]);

    const violations = validateRateChangeFormValues(formValues);

    if (violations.length === 0) {
      const promise = updatePaymentExchangeRate(
        paymentId,
        formValues.m8RateDTO
      ).then(onEdited);

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(violations).then(setFormViolations);
    }
  }, [
    formValues,
    injectPromise,
    onEdited,
    paymentId,
    setFormViolations,
    updatePaymentExchangeRate,
  ]);

  const refreshRate = React.useCallback(() => {
    setFormViolations([]);

    const violations = validateRateChangeFormValues(formValues);

    if (violations.length === 0 && formValues.nextDateOn) {
      const promise = getExchangeRate({
        date: formValues.nextDateOn,
        currencyFrom: formValues.m8RateDTO.currency,
        currencyTo: formValues.m8RateDTO.valueCurrency,
      }).then(setM8RateDTO);

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(violations).then(setFormViolations);
    }
  }, [
    formValues,
    getExchangeRate,
    injectPromise,
    setFormViolations,
    setM8RateDTO,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={formViolations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <GiroPaymentManagerRateChangeFieldset
                disabled={asyncActive}
                values={formValues}
                violations={formViolations}
                onNextDateChange={setNextDateOn}
                onNextDateBlur={refreshRate}
              />
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent
                disabled={asyncActive}
                theme="primary"
                type="submit"
              >
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
